/**
 * API client with in-memory access-token storage and automatic token refresh
 * on 401 responses (single retry).
 *
 * Auth flow:
 *   - POST /auth/login  → { accessToken } + httpOnly refresh cookie
 *   - POST /auth/refresh (cookie) → { accessToken }
 *   - Access token is held in module scope — never in localStorage.
 */

const API_BASE = (import.meta as { env?: { VITE_API_BASE?: string } }).env
  ?.VITE_API_BASE ?? "/api";

// ---------------------------------------------------------------------------
// Token store (module-scoped; not persisted)
// ---------------------------------------------------------------------------

let _token: string | null = null;

export function setToken(token: string | null): void {
  _token = token;
}

export function getToken(): string | null {
  return _token;
}

// ---------------------------------------------------------------------------
// Low-level fetch
// ---------------------------------------------------------------------------

function buildHeaders(
  hasBody: boolean,
  extra?: HeadersInit
): Record<string, string> {
  const headers: Record<string, string> = {
    // Only declare a JSON body when one is actually sent. Setting
    // Content-Type: application/json on a bodyless POST makes Fastify reject
    // it with FST_ERR_CTP_EMPTY_JSON_BODY (e.g. webauthn/register/options,
    // /auth/refresh, /auth/logout).
    ...(hasBody ? { "Content-Type": "application/json" } : {}),
    ...(extra as Record<string, string>),
  };
  if (_token) {
    headers["Authorization"] = `Bearer ${_token}`;
  }
  return headers;
}

async function rawFetch(
  path: string,
  init: RequestInit = {}
): Promise<Response> {
  const url = `${API_BASE}${path}`;
  return fetch(url, {
    ...init,
    headers: buildHeaders(init.body != null, init.headers as HeadersInit),
  });
}

// ---------------------------------------------------------------------------
// Refresh
// ---------------------------------------------------------------------------

async function refreshToken(): Promise<void> {
  const res = await rawFetch("/auth/refresh", { method: "POST" });
  if (!res.ok) {
    setToken(null);
    throw new Error(`Token refresh failed: ${res.status}`);
  }
  const data = (await res.json()) as { accessToken: string };
  setToken(data.accessToken);
}

// ---------------------------------------------------------------------------
// Public request() — retries once after a 401
// ---------------------------------------------------------------------------

/** Parse a JSON body, tolerating empty 204 No Content responses (e.g. DELETE). */
function parseBody<T>(res: Response): Promise<T> {
  if (res.status === 204) return Promise.resolve(undefined as T);
  return res.json() as Promise<T>;
}

export async function request<T = unknown>(
  path: string,
  init: RequestInit = {},
  _isRetry = false
): Promise<T> {
  const res = await rawFetch(path, init);

  if (res.status === 401 && !_isRetry) {
    // Attempt a single token refresh
    await refreshToken(); // throws (and clears token) if refresh fails
    // Retry the original request with the new token
    const retryRes = await rawFetch(path, init);
    if (!retryRes.ok) {
      throw new Error(`Request failed after refresh: ${retryRes.status}`);
    }
    return parseBody<T>(retryRes);
  }

  if (!res.ok) {
    throw new Error(`Request failed: ${res.status}`);
  }

  return parseBody<T>(res);
}

// ---------------------------------------------------------------------------
// Public upload() — multipart/form-data POST with the same 401-refresh retry.
//
// Cannot go through request(): that forces "Content-Type: application/json",
// whereas a FormData body needs the browser to set the multipart boundary. We
// attach the Bearer token and reuse refreshToken() so an expired access token
// is transparently refreshed (the previous raw-fetch upload sent no token at
// all → always 401).
// ---------------------------------------------------------------------------

export async function upload<T = unknown>(
  path: string,
  form: FormData,
  _isRetry = false
): Promise<T> {
  const url = `${API_BASE}${path}`;
  const headers: Record<string, string> = {};
  if (_token) headers["Authorization"] = `Bearer ${_token}`;

  const res = await fetch(url, { method: "POST", headers, body: form });

  if (res.status === 401 && !_isRetry) {
    await refreshToken(); // throws (and clears token) if refresh fails
    return upload<T>(path, form, true);
  }

  if (!res.ok) {
    throw new Error(`Upload failed: ${res.status}`);
  }

  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Auth helpers
// ---------------------------------------------------------------------------

async function login(identifier: string, password: string): Promise<void> {
  const res = await rawFetch("/auth/login", {
    method: "POST",
    body: JSON.stringify({ username: identifier, password }),
  });
  if (!res.ok) {
    // Surface the server error code (e.g. "not_enrolled_this_year") so the UI
    // can show a specific message; fall back to the status.
    let code = "";
    try {
      code = ((await res.json()) as { error?: string }).error ?? "";
    } catch {
      /* non-JSON error body */
    }
    throw new Error(code || `Login failed: ${res.status}`);
  }
  const data = (await res.json()) as { accessToken: string };
  setToken(data.accessToken);
}

async function logout(): Promise<void> {
  try {
    await rawFetch("/auth/logout", { method: "POST" });
  } finally {
    setToken(null);
  }
}

// ---------------------------------------------------------------------------
// Exported client object
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Public downloadBlob() — authenticated GET of a binary file (documents).
// Same Bearer token + single 401-refresh retry as request(); the body is
// returned as a Blob instead of being parsed as JSON.
// ---------------------------------------------------------------------------

export async function downloadBlob(path: string, _isRetry = false): Promise<Blob> {
  const url = `${API_BASE}${path}`;
  const headers: Record<string, string> = {};
  if (_token) headers["Authorization"] = `Bearer ${_token}`;

  const res = await fetch(url, { method: "GET", headers });

  if (res.status === 401 && !_isRetry) {
    await refreshToken(); // throws (and clears token) if refresh fails
    return downloadBlob(path, true);
  }

  if (!res.ok) {
    throw new Error(`Download failed: ${res.status}`);
  }

  return res.blob();
}

export const apiClient = {
  request,
  upload,
  downloadBlob,
  login,
  logout,
};
